import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useProductForm } from './hooks/useCreateProducts'

import { Badge } from '@/components/ui/badge'
import LayoutInside from '@/layout/LayoutInside'

import { useListCategories } from '@/hooks/useListCategories'
import { useNavigate } from 'react-router-dom'

import Step1 from './Partials/Step1'
import Step2 from './Partials/Step2'
import Step3 from './Partials/Step3'
import Step4VerifySupportEmail from './Partials/Step4VerifySupportEmail.tsx'
import Step5Offer from './Partials/Step4'
import Step6Delivery from './Partials/Step5'
import StepRegulatoryDocuments from './Partials/StepRegulatoryDocuments'
import { REQUIRED_CREATE_PRODUCT_REGULATORY_TYPES } from '@/components/Products/regulatory-document-types'
import { ProductsService } from '@/services/products.service'
import type { PendingRegulatoryDocument } from '@/@types/Products'
import {
  isValidHttpUrl,
  normalizeUrlOnBlur,
  isCompleteHttpUrl,
} from '@/utils/normalize'

import { useQuery } from '@tanstack/react-query'
import { useToast } from '@/hooks/use-toast'

export default function ProductForm() {
  const navigate = useNavigate()
  const { toast } = useToast()

  const { t } = useTranslation(['common', 'onboarding', 'validation'])

  const normalizeEmail = (value: string | null | undefined) =>
    (value || '').trim().toLowerCase()

  const breadcrumbItems = [
    { text: t('common:products'), link: '/products' },
    { text: t('common:create_product'), link: '/products/create-products' },
  ]

  const [step, setStep] = useState(1)
  const [verifiedSupportEmail, setVerifiedSupportEmail] = useState<
    string | null
  >(null)
  const [offerStepCameFromVerify, setOfferStepCameFromVerify] = useState(false)
  const [isCheckingSupportEmail, setIsCheckingSupportEmail] = useState(false)
  const [pendingRegulatoryDocs, setPendingRegulatoryDocs] = useState<
    PendingRegulatoryDocument[]
  >([])
  const verifiedSupportEmailRef = useRef<string | null>(null)
  const pendingRegulatoryDocsRef = useRef(pendingRegulatoryDocs)

  useEffect(() => {
    pendingRegulatoryDocsRef.current = pendingRegulatoryDocs
  }, [pendingRegulatoryDocs])

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    getValues,
    errors,
    onSubmit,
    setError,
    clearErrors,
    isSubmitting,
    isCreatingProduct,
    reset,
  } = useProductForm({
    afterProductCreated: async (productId) => {
      const docs = pendingRegulatoryDocsRef.current
      for (const doc of docs) {
        await ProductsService.uploadRegulatoryDocuments({
          productId,
          documents: [
            {
              file: doc.file,
              document_name: doc.name,
              document_type: doc.type || undefined,
              note: doc.notes || undefined,
            },
          ],
        })
      }
    },
  })

  const selectedType = watch('product_type') ?? ''
  const isNutraProduct = selectedType === 'pharmaceutical'
  const totalSteps = isNutraProduct ? 7 : 6

  const defaultValues = watch()

  const { categories } = useListCategories({
    categoryType: defaultValues?.product_type || 'digital',
  })

  const { data: hasAnyProducts, isPending: isCheckingFirstProduct } = useQuery({
    queryKey: ['products', 'hasAny'],
    queryFn: async () => {
      const response = await ProductsService.getProducts({
        currentPage: 1,
        query: '',
        status: 'all',
      })

      return (response?.data?.length ?? 0) > 0
    },
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: 1,
  })

  const isFirstProduct = hasAnyProducts === false

  useEffect(() => {
    if (errors.whatsapp_support) {
      clearErrors('whatsapp_support')
    }
  }, [watch('whatsapp_support'), clearErrors])

  useEffect(() => {
    verifiedSupportEmailRef.current = verifiedSupportEmail
  }, [verifiedSupportEmail])

  useEffect(() => {
    if (defaultValues?.offer_payment_type) {
      clearErrors('offer_payment_type')
    }

    if (defaultValues?.offer_price) {
      clearErrors('offer_price')
    }

    if (defaultValues?.product_delivery) {
      clearErrors('product_delivery')
    }

    if (defaultValues?.offer_trial_mode) {
      clearErrors('offer_trial_mode')
    }

    if (defaultValues?.offer_trial_interval) {
      clearErrors('offer_trial_interval')
    }

    if (defaultValues?.offer_trial_price) {
      clearErrors('offer_trial_price')
    }
  }, [
    defaultValues.offer_payment_type,
    defaultValues?.offer_price,
    defaultValues?.product_delivery,
    defaultValues?.offer_trial_mode,
    defaultValues?.offer_trial_interval,
    defaultValues?.offer_trial_price,
  ])

  const validateStep1 = () => {
    if (!selectedType) {
      setError('product_type', {
        type: 'manual',
        message: t('validationProductType'),
      })
      return false
    } else {
      clearErrors('product_type')
      return true
    }
  }

  const hasBlacklistedTerm = (value?: string | null) => {
    if (!value) return false

    const blacklistTerms = [
      'google',
      'tiktok',
      'whats',
      'whatsapp',
      'facebook',
      'face',
      'ytb',
      'youtube',
      'spy',
      'hacker',
    ]

    const lowerValue = value.toLowerCase()

    return blacklistTerms.some((term) =>
      lowerValue.includes(term.toLowerCase()),
    )
  }

  const validateSalesPageUrl = (
    url: string,
  ): { isValid: boolean; message?: string } => {
    try {
      const urlObj = new URL(url)
      const host = urlObj.hostname.toLowerCase().replace(/^www\./, '')
      const path = urlObj.pathname
      const hasPath = path && path !== '/'

      // Domínios totalmente bloqueados (com ou sem path)
      const fullyBlockedDomains = [
        'facebook.com',
        'google.com',
        'twitter.com',
        'linkedin.com',
      ]

      // Domínios bloqueados apenas sem path (permitidos com path)
      const blockedWithoutPathDomains = ['youtube.com', 'instagram.com']

      // Verifica domínios totalmente bloqueados
      for (const blockedDomain of fullyBlockedDomains) {
        if (host === blockedDomain || host.endsWith('.' + blockedDomain)) {
          return {
            isValid: false,
            message: t('validationSalesPageBlocked'),
          }
        }
      }

      // Verifica domínios bloqueados apenas sem path
      for (const blockedDomain of blockedWithoutPathDomains) {
        if (host === blockedDomain || host.endsWith('.' + blockedDomain)) {
          if (!hasPath) {
            return {
              isValid: false,
              message: t('validationSalesPageBlocked'),
            }
          }
          // Se tiver path, permite
          return { isValid: true }
        }
      }

      return { isValid: true }
    } catch {
      return { isValid: false, message: t('validationSalesPageBlocked') }
    }
  }

  const validateStep2 = () => {
    let valid = true
    const values = getValues()
    const name = String(values.name ?? '').trim()
    const description = String(values.description ?? '').trim()
    const categoryRaw = values.category_id
    const category =
      categoryRaw == null || categoryRaw === ''
        ? ''
        : String(categoryRaw).trim()
    const salesPage = String(values.sales_page ?? '').trim()
    const sku = String(values.sku ?? '').trim()
    const productType = String(values.product_type ?? '').trim()
    const supportUrl = watch('support_url')
    const privacyTermsUrl = watch('privacy_terms_url')
    const normalizedSalesPage = normalizeUrlOnBlur(salesPage || '')
    const normalizedSupportUrl = normalizeUrlOnBlur(supportUrl || '')
    const normalizedPrivacyTermsUrl = normalizeUrlOnBlur(privacyTermsUrl || '')

    if (!name) {
      setError('name', {
        type: 'manual',
        message: t('required'),
      })
      valid = false
    } else if (hasBlacklistedTerm(name)) {
      setError('name', {
        type: 'manual',
        message: t('validationProductNameBlacklist'),
      })
      valid = false
    } else {
      clearErrors('name')
    }

    clearErrors('offer_name')

    if (!category) {
      setError('category_id', {
        type: 'manual',
        message: t('required'),
      })
      valid = false
    } else {
      clearErrors('category_id')
    }

    if (productType === 'pharmaceutical') {
      if (!sku) {
        setError('sku', {
          type: 'manual',
          message: t('required'),
        })
        valid = false
      } else {
        clearErrors('sku')
      }
    } else {
      clearErrors('sku')
    }

    if (!description) {
      setError('description', {
        type: 'manual',
        message: t('validationDescriptionRequired'),
      })
      valid = false
    } else if (description.length < 40) {
      setError('description', {
        type: 'manual',
        message: t('validationDescriptionMinLength'),
      })
      valid = false
    } else if (description.length > 2000) {
      setError('description', {
        type: 'manual',
        message: t('validationDescriptionMaxLength'),
      })
      valid = false
    } else {
      clearErrors('description')
    }

    const incompleteUrl =
      salesPage === 'https://' ||
      salesPage === 'http://' ||
      salesPage === 'https:' ||
      salesPage === 'http:'

    if (!salesPage || incompleteUrl) {
      setError('sales_page', {
        type: 'manual',
        message: t('validationSalesPage'),
      })
      valid = false
    } else if (!isValidHttpUrl(normalizedSalesPage)) {
      setError('sales_page', {
        type: 'manual',
        message: t('validationInvalidSalesPage'),
      })
      valid = false
    } else {
      // Valida blacklist de domínios
      const urlValidation = validateSalesPageUrl(normalizedSalesPage)
      if (!urlValidation.isValid) {
        setError('sales_page', {
          type: 'manual',
          message: urlValidation.message || t('validationInvalidSalesPage'),
        })
        valid = false
      } else {
        clearErrors('sales_page')
      }
    }

    // thankyou_url opcional: vazio ok; qualquer texto precisa ser URL completa (com host)
    const thankyouRaw = (watch('thankyou_url') ?? '').trim().replace(/\s/g, '')
    if (!thankyouRaw) {
      clearErrors('thankyou_url')
      setValue('thankyou_url', '')
    } else {
      const normalizedThankyou = normalizeUrlOnBlur(thankyouRaw)
      if (!isCompleteHttpUrl(normalizedThankyou)) {
        setError('thankyou_url', {
          type: 'manual',
          message: t('validationInvalidThankyouUrl'),
        })
        valid = false
      } else if (normalizedThankyou.length > 500) {
        setError('thankyou_url', {
          type: 'manual',
          message: t('validationUrlTooLong', { max: 500 }),
        })
        valid = false
      } else {
        setValue('thankyou_url', normalizedThankyou)
        clearErrors('thankyou_url')
      }
    }

    // No 1º produto, reaproveita a URL de vendas como page_url (campo exigido pelo backend)
    if (isFirstProduct && normalizedSalesPage) {
      setValue('page_url', normalizedSalesPage)
    }

    if (isFirstProduct) {
      if (!supportUrl) {
        setError('support_url', {
          type: 'manual',
          message: t('required'),
        })
        valid = false
      } else if (normalizedSupportUrl.length > 255) {
        setError('support_url', {
          type: 'manual',
          message: t('validationUrlTooLong'),
        })
        valid = false
      } else if (!isValidHttpUrl(normalizedSupportUrl)) {
        setError('support_url', {
          type: 'manual',
          message: t('validationInvalidSupportUrl'),
        })
        valid = false
      }

      if (!privacyTermsUrl) {
        setError('privacy_terms_url', {
          type: 'manual',
          message: t('required'),
        })
        valid = false
      } else if (normalizedPrivacyTermsUrl.length > 255) {
        setError('privacy_terms_url', {
          type: 'manual',
          message: t('validationUrlTooLong'),
        })
        valid = false
      } else if (!isValidHttpUrl(normalizedPrivacyTermsUrl)) {
        setError('privacy_terms_url', {
          type: 'manual',
          message: t('validationInvalidPrivacyUrl'),
        })
        valid = false
      }
    }

    // Se já falhou nas validações anteriores, não chama API
    if (!valid) {
      return false
    }

    return true
  }

  const validateEmail = (
    email: string,
  ): { isValid: boolean; message?: string } => {
    if (!email) {
      return { isValid: false, message: t('validationSupportEmail') }
    }

    // Validação sintática mais robusta
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/

    if (!emailRegex.test(email)) {
      return { isValid: false, message: t('validationInvalidSupportEmail') }
    }

    // Verifica se o domínio tem pelo menos um ponto e extensão válida
    const parts = email.split('@')
    if (parts.length !== 2) {
      return { isValid: false, message: t('validationInvalidSupportEmail') }
    }

    const domain = parts[1]
    const domainParts = domain.split('.')

    // Verifica se tem pelo menos domínio e TLD (ex: example.com)
    if (
      domainParts.length < 2 ||
      domainParts[domainParts.length - 1].length < 2
    ) {
      return { isValid: false, message: t('validationInvalidSupportEmail') }
    }

    // Verifica se não tem espaços
    if (email.includes(' ')) {
      return { isValid: false, message: t('validationInvalidSupportEmail') }
    }

    // Verifica tamanho máximo (RFC 5321)
    if (email.length > 254) {
      return { isValid: false, message: t('validationInvalidSupportEmail') }
    }

    return { isValid: true }
  }

  const validateStep3 = () => {
    let valid = true

    const email = (watch('email_support') || '').trim()
    const whatsapp = watch('whatsapp_support')

    // Validação de email melhorada
    const emailValidation = validateEmail(email)
    if (!emailValidation.isValid) {
      setError('email_support', {
        type: 'manual',
        message: emailValidation.message || t('validationInvalidSupportEmail'),
      })
      valid = false
    } else {
      clearErrors('email_support')
    }

    if (!whatsapp) {
      setError('whatsapp_support', {
        type: 'manual',
        message: t('validationWhatsApp'),
      })
      valid = false
    }

    // Remove caracteres não numéricos para validar apenas os dígitos
    const digitsOnly = whatsapp.replace(/\D/g, '')

    if (digitsOnly.length < 9) {
      setError('whatsapp_support', {
        type: 'manual',
        message: t('validationWhatsAppMinLength'),
      })
      valid = false
    } else if (digitsOnly.length > 15) {
      setError('whatsapp_support', {
        type: 'manual',
        message: t('validationInvalidWhatsApp'),
      })
      valid = false
    }

    return valid
  }

  const validateStep4 = () => {
    const email = (watch('email_support') || '').trim()
    const verifiedNow = verifiedSupportEmailRef.current

    if (normalizeEmail(verifiedNow) !== normalizeEmail(email)) {
      setError('email_support', {
        type: 'manual',
        message: t('validationSupportEmailMustVerify'),
      })
      return false
    }

    clearErrors('email_support')
    return true
  }

  const validateStep5 = () => {
    let valid = true

    if (
      watch('offer_payment_type') === 'recurring' &&
      watch('offer_currency') !== 'BRL'
    ) {
      setValue('offer_currency', 'BRL')
    }

    if (!watch('offer_payment_type')) {
      setError('offer_payment_type', {
        type: 'manual',
        message: t('validationPaymentType'),
      })
      valid = false
    }

    if (!watch('offer_price') || watch('offer_price') === 0) {
      setError('offer_price', {
        type: 'manual',
        message: t('validationOfferPrice'),
      })
      valid = false
    } else if (watch('offer_price') < 500) {
      setError('offer_price', {
        type: 'manual',
        message: t('validationOfferPriceMin', {
          min: `${5 + ' ' + defaultValues.offer_currency}`,
        }),
      })
      valid = false
    }

    if (
      watch('offer_payment_type') === 'recurring' &&
      !watch('offer_recurring_interval')
    ) {
      setError('offer_recurring_interval', {
        type: 'manual',
        message: t('required_field'),
      })
      valid = false
    }

    if (
      watch('offer_payment_type') === 'recurring' &&
      watch('offer_trial_enabled')
    ) {
      if (!watch('offer_trial_mode')) {
        setError('offer_trial_mode', {
          type: 'manual',
          message: t('required_field'),
        })
        valid = false
      }

      if (!watch('offer_trial_interval')) {
        setError('offer_trial_interval', {
          type: 'manual',
          message: t('required_field'),
        })
        valid = false
      }

      const intervalDays: Record<string, number> = {
        daily: 1,
        weekly: 7,
        monthly: 30,
        quarterly: 90,
        semiannual: 180,
        yearly: 365,
      }
      const trialInterval = watch('offer_trial_interval')
      const recurringInterval = watch('offer_recurring_interval')

      if (
        trialInterval &&
        recurringInterval &&
        (intervalDays[trialInterval] ?? 0) >= (intervalDays[recurringInterval] ?? 0)
      ) {
        setError('offer_trial_interval', {
          type: 'manual',
          message: t('trial_free_longer_than_interval'),
        })
        valid = false
      }

      if (watch('offer_trial_mode') === 'promotional') {
        const trialPrice = watch('offer_trial_price')
        if (!trialPrice || trialPrice < 1) {
          setError('offer_trial_price', {
            type: 'manual',
            message: t('required_field'),
          })
          valid = false
        } else if (trialPrice >= watch('offer_price')) {
          setError('offer_trial_price', {
            type: 'manual',
            message: t('trial_price_lt_offer'),
          })
          valid = false
        }
      }
    }

    return valid
  }

  const validateStep6 = () => {
    let valid = true

    const productEmail = watch('product_email')
    const productPassword = watch('product_password')
    const productUrl = watch('product_url')
    const normalizedProductUrl = normalizeUrlOnBlur(productUrl || '')
    const productEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    const isNoDelivery = watch('product_delivery') === 'no_delivery'
    const isDigital = watch('product_type') === 'digital'

    if (isDigital && isNoDelivery) {
      if (!productEmail) {
        setError('product_email', {
          type: 'manual',
          message: t('required_field'),
        })
        valid = false
      } else if (!productEmailRegex.test(productEmail)) {
        setError('product_email', {
          type: 'manual',
          message: t('validation:email_invalid'),
        })
        valid = false
      }

      if (!productPassword) {
        setError('product_password', {
          type: 'manual',
          message: t('required_field'),
        })
        valid = false
      }

      if (!productUrl) {
        setError('product_url', {
          type: 'manual',
          message: t('required_field'),
        })
        valid = false
      } else if (!isValidHttpUrl(normalizedProductUrl)) {
        setError('product_url', {
          type: 'manual',
          message: t('validationInvalidSalesPage'),
        })
        valid = false
      }
    }

    if (!watch('product_delivery')) {
      setError('product_delivery', {
        type: 'manual',
        message: t('required_field'),
      })
      valid = false
    }

    return valid
  }

  const validateRegulatoryStep = () => {
    const hasAllRequiredDocuments =
      REQUIRED_CREATE_PRODUCT_REGULATORY_TYPES.every((requiredType) =>
        pendingRegulatoryDocs.some((doc) => doc.type === requiredType),
      )

    if (!hasAllRequiredDocuments) {
      toast({
        variant: 'error',
        description: t('common:regulatoryDocumentsRequiredItems', {
          defaultValue:
            'Envie os 3 documentos obrigatórios para continuar.',
        }),
      })
      return false
    }

    return true
  }

  const handleNext = async () => {
    if (step === 1) {
      if (validateStep1()) setStep(2)
      return
    }

    if (step === 2) {
      if (validateStep2()) setStep(3)
      return
    }

    if (step === 3) {
      if (!validateStep3()) return

      const email = (watch('email_support') || '').trim()
      const verifiedNow = verifiedSupportEmailRef.current
      const isVerifiedForThisEmail =
        normalizeEmail(verifiedNow) === normalizeEmail(email)

      if (isVerifiedForThisEmail) {
        setOfferStepCameFromVerify(false)
        setStep(5) // pula step de verificação
        return
      }

      if (isCheckingSupportEmail) return

      setIsCheckingSupportEmail(true)

      try {
        const response = await ProductsService.validateSupportEmail(
          email,
          watch('name'),
          { send: false },
        )
        const alreadyVerified = Boolean(response?.data?.already_verified)

        if (alreadyVerified) {
          const nextVerified = email.trim()
          verifiedSupportEmailRef.current = nextVerified
          setVerifiedSupportEmail(nextVerified)
          clearErrors('email_support')
          setOfferStepCameFromVerify(false)
          setStep(5)
          return
        }

        setStep(4)
      } catch (error) {
        const errorCode = (
          error as { response?: { data?: { error_code?: string } } }
        )?.response?.data?.error_code

        if (errorCode === 'invalid_domain') {
          setError('email_support', {
            type: 'manual',
            message: t('supportEmailInvalidDomain'),
          })
        } else {
          toast({
            variant: 'error',
            description: t('supportEmailVerificationErrorValidating'),
          })
        }
      } finally {
        setIsCheckingSupportEmail(false)
      }
      return
    }

    if (step === 4) {
      if (validateStep4()) {
        setOfferStepCameFromVerify(true)
        setStep(5)
      }
      return
    }

    if (step === 5) {
      if (validateStep5()) setStep(6)
      return
    }

    if (step === 6) {
      if (isNutraProduct) {
        if (validateRegulatoryStep()) setStep(7)
      } else if (validateStep6()) {
        onSubmit(defaultValues)
      }
      return
    }

    if (step === 7 && validateStep6()) {
      onSubmit(defaultValues)
    }
  }

  const handleBack = () => {
    if (step === 1) {
      navigate(-1)
    } else if (step === 2) {
      setStep(1)
      clearErrors()
      reset()
      setPendingRegulatoryDocs([])
    } else if (step === 7) {
      setStep(6)
    } else if (step === 5) {
      const email = (watch('email_support') || '').trim()
      const verifiedNow = verifiedSupportEmailRef.current
      const isVerifiedForThisEmail =
        normalizeEmail(verifiedNow) === normalizeEmail(email)

      setStep(isVerifiedForThisEmail ? 3 : offerStepCameFromVerify ? 4 : 3)
    } else {
      setStep(step - 1)
    }
  }

  return (
    <LayoutInside items={breadcrumbItems}>
      <div className="w-full m-auto text-white px-4 min-h-[calc(100vh-120px)] flex items-center justify-center flex-col">
        <div className="flex items-center justify-between mb-2">
          <Badge variant="secondary" className="text-xs m-auto">
            {t('common:stepOf', { step, total: totalSteps })}
          </Badge>
        </div>
        <section className="w-full flex items-center justify-center flex-col max-sm:pb-16">
          {step === 1 && (
            <Step1
              onProductTypeSelected={(type) => {
                setValue('product_type', type.value)
                setStep(2)
              }}
              selectedType={selectedType}
            />
          )}
          {step === 2 && (
            <Step2
              watch={watch}
              register={register}
              errors={errors}
              setValue={setValue}
              clearErrors={clearErrors}
              defaultValues={defaultValues}
              handleBack={handleBack}
              handleNext={handleNext}
              categories={categories}
              isFirstProduct={isFirstProduct}
              isCheckingFirstProduct={isCheckingFirstProduct}
            />
          )}

          {step === 3 && (
            <Step3
              register={register}
              errors={errors}
              setValue={setValue}
              watch={watch}
              handleBack={handleBack}
              handleNext={handleNext}
              isCheckingSupportEmail={isCheckingSupportEmail}
            />
          )}

          {step === 4 && (
            <Step4VerifySupportEmail
              watch={watch}
              setError={setError}
              clearErrors={clearErrors}
              verifiedSupportEmail={verifiedSupportEmail}
              onSupportEmailVerified={(email) => {
                const nextVerified = email.trim()
                verifiedSupportEmailRef.current = nextVerified
                setVerifiedSupportEmail(nextVerified)
                clearErrors('email_support')
              }}
              handleBack={handleBack}
              handleNext={handleNext}
            />
          )}

          {step === 5 && (
            <Step5Offer
              watch={watch}
              errors={errors}
              setValue={setValue}
              defaultValues={defaultValues}
              handleBack={handleBack}
              handleNext={handleNext}
            />
          )}

          {step === 6 && isNutraProduct && (
            <StepRegulatoryDocuments
              documents={pendingRegulatoryDocs}
              onDocumentsChange={setPendingRegulatoryDocs}
              handleBack={handleBack}
              handleNext={handleNext}
            />
          )}

          {step === 6 && !isNutraProduct && (
            <Step6Delivery
              watch={watch}
              register={register}
              errors={errors}
              setValue={setValue}
              clearErrors={clearErrors}
              defaultValues={defaultValues}
              handleBack={handleBack}
              handleNext={handleNext}
              isSubmitting={isCreatingProduct || isSubmitting}
            />
          )}

          {step === 7 && isNutraProduct && (
            <Step6Delivery
              watch={watch}
              register={register}
              errors={errors}
              setValue={setValue}
              clearErrors={clearErrors}
              defaultValues={defaultValues}
              handleBack={handleBack}
              handleNext={handleNext}
              isSubmitting={isCreatingProduct || isSubmitting}
            />
          )}
        </section>
      </div>
    </LayoutInside>
  )
}
